import "@Front/index.scss";
import { DefaultLayout } from "@Front/Components/LayoutTemplates/DefaultLayout";
import type { NextPage } from "next";
import type { AppType, AppProps } from "next/app";
import { type ReactElement, type ReactNode } from "react";
import { GoogleTagManager } from "@next/third-parties/google";
import { Poppins } from "next/font/google";
import { RolePermissionsProvider } from "@Front/Stores/RolePermissionsStore";
import { SiteTextsProvider } from "@Front/Stores/SiteTextsContext";
import { SITE_TEXTS_BOOT_SCOPES, SITE_TEXTS_BOOT_LOCALE } from "@Front/Stores/siteTextsBootConfig";
import { AppFontsStyle } from "@Front/Components/AppRoot/AppFontsStyle";
import { useAppFrontendBootstrap } from "@Front/Hooks/useAppFrontendBootstrap";
import { PublicSiteConfigProvider } from "@Front/Services/PublicSiteConfig/usePublicUiOptions";
import { PublicSiteThemeEffect } from "@Front/themes/useApplyPublicSiteTheme";
import { getPublicSiteCode } from "@Front/themes/siteTheme";

const INTER_FONT_FAMILY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const poppins = Poppins({
	subsets: ["latin"],
	weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
	style: ["normal", "italic"],
	display: "swap",
	variable: "--font-poppins",
	preload: false,
});

export type NextPageWithLayout<TProps = Record<string, unknown>, TInitialProps = TProps> = NextPage<TProps, TInitialProps> & {
	getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

function AppShell({ Component, pageProps }: AppPropsWithLayout): ReactElement {
	const getLayout: (page: ReactElement) => ReactNode =
		Component.getLayout ?? ((page: ReactElement): ReactElement => <DefaultLayout children={page}></DefaultLayout>);

	useAppFrontendBootstrap();

	const page = (
		<>
			<AppFontsStyle interFontFamily={INTER_FONT_FAMILY} poppinsFontFamily={poppins.style.fontFamily} />
			<Component {...pageProps} />
			<GoogleTagManager gtmId="GTM-5GLJN86P" />
		</>
	);

	return (
		<div className={`${poppins.variable}`} data-lecoffre-site={getPublicSiteCode()}>
			<SiteTextsProvider scopes={[...SITE_TEXTS_BOOT_SCOPES]} locale={SITE_TEXTS_BOOT_LOCALE}>
				<PublicSiteConfigProvider>
					<PublicSiteThemeEffect />
					<RolePermissionsProvider>{getLayout(page)}</RolePermissionsProvider>
				</PublicSiteConfigProvider>
			</SiteTextsProvider>
		</div>
	);
}

const MyApp = AppShell as AppType;

export default MyApp;
